<template>
    <div class="flex flex-col gap-4">
        <div v-if="previewQuestions.length === 0" class="body-small text-valence-grey-750">Add a question to see the participant preview.</div>
        <template v-else>
            <div>
                <div v-if="topSection" class="flex items-baseline justify-between gap-3 mb-3">
                    <h1 class="body-bold text-valence-grey-850 leading-tight">{{ topSection.title }}</h1>
                    <AssessmentEyebrow v-if="childSection">{{ childSection.title }}</AssessmentEyebrow>
                </div>
                <div class="flex items-center justify-between details font-medium text-valence-grey-750 mb-3">
                    <span>{{ progressLabel }}</span>
                </div>
                <BaseProgressBar :value="progressPct" size="xs" :aria-label="`Progress: ${progressPct}%`" />
            </div>

            <SectionIntroCard v-if="showingSectionIntro && topSection" :section="topSection" :question-count="topSectionQuestionCount" />

            <template v-else-if="isPaged">
                <div v-for="item in pageItems" :key="item.question.id" class="flex flex-col gap-3">
                    <div v-if="item.subheading" class="details font-semibold uppercase tracking-wider text-valence-primary-500">{{ item.subheading }}</div>
                    <QuestionCard
                        :question="item.question"
                        :score="answers[item.question.id]?.score ?? null"
                        :text="answers[item.question.id]?.text ?? ''"
                        :not-observed="answers[item.question.id]?.notObserved ?? false"
                        :allow-score-examples="allowScoreExamples"
                        :allow-not-observed="allowNotObserved"
                        @update:score="setScore(item.question.id, $event)"
                        @update:text="setText(item.question.id, $event)"
                        @update:not-observed="setNotObserved(item.question.id, $event)" />
                </div>
            </template>

            <QuestionCard
                v-else-if="currentQuestion"
                :question="currentQuestion"
                :score="answers[currentQuestion.id]?.score ?? null"
                :text="answers[currentQuestion.id]?.text ?? ''"
                :not-observed="answers[currentQuestion.id]?.notObserved ?? false"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                @update:score="setScore(currentQuestion.id, $event)"
                @update:text="setText(currentQuestion.id, $event)"
                @update:not-observed="setNotObserved(currentQuestion.id, $event)" />

            <div class="flex items-center justify-between">
                <button
                    type="button"
                    class="body-small text-valence-grey-750 hover:text-valence-grey-850 disabled:opacity-30 flex items-center gap-1"
                    :disabled="atStart"
                    @click="goPrev">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
                </button>
                <BaseButton v-if="showingSectionIntro" theme="primary" class="inline-flex items-center gap-1" @click="showingSectionIntro = false">
                    Begin section <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton v-else-if="!atEnd" theme="primary" class="inline-flex items-center gap-1" @click="goNext"
                    >Next <i class="bi bi-arrow-right" aria-hidden="true"></i
                ></BaseButton>
                <BaseButton v-else theme="primary" disabled>Submit</BaseButton>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
/**
 * Participant's-eye view of the survey being edited.
 *
 * Renders the real rater components (`QuestionCard`, `SectionIntroCard`) from
 * unsaved builder state and pages through them with the same grouping helpers
 * the rater flow uses, so the preview stays honest without creating a response.
 */
import type { SectionSummary } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import QuestionCard, { type QuestionCardItem } from "~vue/components/QuestionCard.vue";
import SectionIntroCard from "~vue/components/SectionIntroCard.vue";
import type { BuilderChoices, BuilderDocument } from "~vue/SurveyBuilder/types";
import {
    buildChunkedPages,
    buildPageItems,
    buildSectionPages,
    childSectionForCode,
    opensTopSection,
    questionCountForTopSection,
    sectionsByCode as toSectionsByCode,
    topSectionForCode,
} from "~vue/utils/assessmentSections";
import { computed, reactive, ref, watch } from "vue";

/** A question as the participant renderers see it: card content plus its section. */
type PreviewQuestion = QuestionCardItem & { section_code: string | null };

interface PreviewAnswer {
    score: number | null;
    text: string;
    notObserved: boolean;
}

const props = defineProps<{
    document: BuilderDocument;
    choices: BuilderChoices;
}>();

const answers = reactive<Record<string, PreviewAnswer>>({});
const cursor = ref(0);
const showingSectionIntro = ref(false);

const template = computed(() => props.document.template);
// Several questions per page, either one section per page or fixed chunks.
const isPaged = computed(() => template.value.display_mode !== "one_per_page");
// Paginated mode hides sections from participants; the preview shows the
// questions in template order, since the per-response shuffle only happens
// once a real response exists.
const hidesSections = computed(() => template.value.display_mode === "paginated");
const allowScoreExamples = computed(() => !template.value.hide_score_example_composers);
const allowNotObserved = computed(() => !template.value.hide_not_observed_option);

// Sections need a stable code to link questions to, so unsaved sections fall
// back to their client-side ref.
const sectionCodeByRef = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const section of props.document.sections) {
        map[section.ref] = section.code || section.ref;
    }
    return map;
});

const previewSections = computed<SectionSummary[]>(() =>
    props.document.sections
        .filter((section) => section.enabled)
        .map((section) => ({
            id: section.ref,
            code: sectionCodeByRef.value[section.ref],
            title: section.title || "Untitled section",
            description: section.description,
            instructions: section.instructions,
            parent_code: section.parent_ref ? (sectionCodeByRef.value[section.parent_ref] ?? null) : null,
            depth: section.parent_ref ? 1 : 0,
            order: section.order,
        })),
);

const scaleLabelsFor = (scale: string | null) => {
    const option = props.choices.scales.find((candidate) => candidate.value === (scale ?? template.value.default_scale));
    const fallback = { labels: [] };
    if (!option) {
        return fallback;
    }
    return template.value.hide_neutral_response_options ? option.labels_without_neutral : option.labels;
};

const previewQuestions = computed<PreviewQuestion[]>(() =>
    props.document.questions
        .filter((question) => question.enabled)
        .map((question) => ({
            id: question.client_id,
            title: question.title,
            text: question.text || "Untitled question",
            is_score: question.question_type === "Score",
            is_text: question.question_type === "Text",
            is_optional: question.is_optional,
            scale_labels: scaleLabelsFor(question.scale),
            section_code: question.section_ref ? (sectionCodeByRef.value[question.section_ref] ?? null) : null,
        })),
);

const sectionsByCode = computed(() => toSectionsByCode(previewSections.value));
const pages = computed(() =>
    hidesSections.value ? buildChunkedPages(previewQuestions.value, template.value.questions_per_page) : buildSectionPages(previewQuestions.value, sectionsByCode.value),
);

const stepCount = computed(() => (isPaged.value ? pages.value.length : previewQuestions.value.length));
const currentQuestion = computed<PreviewQuestion | null>(() => (isPaged.value ? null : (previewQuestions.value[cursor.value] ?? null)));
const currentPageQuestions = computed(() => (isPaged.value ? (pages.value[cursor.value]?.questions ?? []) : []));
const pageItems = computed(() => {
    if (hidesSections.value) {
        return currentPageQuestions.value.map((question) => ({ question, subheading: null }));
    }
    return buildPageItems(currentPageQuestions.value, sectionsByCode.value);
});

const anchorQuestion = computed<PreviewQuestion | null>(() => (isPaged.value ? (currentPageQuestions.value[0] ?? null) : currentQuestion.value));
const topSection = computed(() => (hidesSections.value ? null : topSectionForCode(sectionsByCode.value, anchorQuestion.value?.section_code)));
const childSection = computed(() => (isPaged.value ? null : childSectionForCode(sectionsByCode.value, anchorQuestion.value?.section_code)));
const topSectionQuestionCount = computed(() => questionCountForTopSection(previewQuestions.value, topSection.value?.code, sectionsByCode.value));

const atStart = computed(() => cursor.value === 0 && !showingSectionIntro.value);
const atEnd = computed(() => cursor.value >= stepCount.value - 1);
const progressPct = computed(() => (stepCount.value === 0 ? 0 : Math.round(((cursor.value + 1) / stepCount.value) * 100)));
const progressLabel = computed(() => {
    if (showingSectionIntro.value) {
        return "About to start this section";
    }
    if (hidesSections.value) {
        return `Page ${cursor.value + 1} of ${stepCount.value}`;
    }
    return isPaged.value ? `Section ${cursor.value + 1} of ${stepCount.value}` : `Question ${cursor.value + 1} of ${stepCount.value}`;
});

function setScore(id: string, score: number) {
    answers[id] = { ...(answers[id] ?? { text: "", notObserved: false, score: null }), score, notObserved: false };
}

function setText(id: string, text: string) {
    answers[id] = { ...(answers[id] ?? { score: null, notObserved: false, text: "" }), text };
}

function setNotObserved(id: string, notObserved: boolean) {
    answers[id] = { ...(answers[id] ?? { score: null, text: "", notObserved: false }), notObserved, score: notObserved ? null : (answers[id]?.score ?? null) };
}

function refreshSectionIntro() {
    showingSectionIntro.value = !isPaged.value && opensTopSection(previewQuestions.value, cursor.value, sectionsByCode.value);
}

function goNext() {
    if (cursor.value < stepCount.value - 1) {
        cursor.value += 1;
        refreshSectionIntro();
    }
}

function goPrev() {
    if (showingSectionIntro.value) {
        showingSectionIntro.value = false;
        return;
    }
    if (cursor.value > 0) {
        cursor.value -= 1;
        refreshSectionIntro();
    }
}

// Editing the survey can shrink or reshuffle the list under the cursor.
watch([stepCount, () => template.value.display_mode], () => {
    cursor.value = Math.min(cursor.value, Math.max(stepCount.value - 1, 0));
    refreshSectionIntro();
});

refreshSectionIntro();

/** Jump the preview to a question the author is editing. */
function focusQuestion(clientId: string) {
    const index = previewQuestions.value.findIndex((question) => question.id === clientId);
    if (index === -1) {
        return;
    }
    cursor.value = isPaged.value ? pages.value.findIndex((page) => page.questions.some((question) => question.id === clientId)) : index;
    showingSectionIntro.value = false;
}

defineExpose({ focusQuestion });
</script>
